/**
 * One row inside a QuadrantCard. Renders severity-coloured count badge
 * linked to the rule's drill_url. Null count -> grey non-clickable dash.
 */
import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import type { AnomalyResult, Severity } from './types';
import PriorityBadge from './PriorityBadge';


const BG: Record<Severity, string> = {
  green:   'var(--color-success-bg, #F0FDF4)',
  orange:  'var(--color-warning-bg, #FEFCE8)',
  red:     'var(--color-danger-bg, #FEF2F2)',
  unknown: 'var(--color-bg-tertiary, #EDEDE8)',
};

const FG: Record<Severity, string> = {
  green:   'var(--color-success, #16A34A)',
  orange:  'var(--color-warning, #CA8A04)',
  red:     'var(--color-danger, #DC2626)',
  unknown: 'var(--color-text-muted, #8A8A85)',
};


const Badge: React.FC<{ count: number | null; severity: Severity }> = ({ count, severity }) => (
  <Box
    data-severity={severity}
    sx={{
      minWidth: 40, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      px: '8px', borderRadius: '6px', fontWeight: 600, fontSize: '13px',
      bgcolor: BG[severity], color: FG[severity],
    }}
  >
    {count === null ? '—' : count}
  </Box>
);


interface Props {
  anomaly: AnomalyResult;
}

const AnomalyRow: React.FC<Props> = ({ anomaly }) => {
  const isClickable = anomaly.count !== null;
  const tooltipText = anomaly.count === null
    ? 'Rule unavailable'
    : anomaly.description;

  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75,
        borderBottom: '1px solid var(--color-border, #eee)',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Tooltip title={tooltipText} arrow placement="left">
        <span>
          {isClickable ? (
            <Link to={anomaly.drill_url} style={{ textDecoration: 'none' }}>
              <Badge count={anomaly.count} severity={anomaly.severity} />
            </Link>
          ) : (
            <Badge count={anomaly.count} severity={anomaly.severity} />
          )}
        </span>
      </Tooltip>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: '13px', color: 'var(--color-text-primary)' }}>
          {anomaly.label}
        </Typography>
        <PriorityBadge priority={anomaly.priority} criticality={anomaly.criticality} />
      </Box>
    </Box>
  );
};

export default AnomalyRow;
